import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BasketEquityChart } from "./BasketViews";

const gbp = (v) => `£${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pf = (v) => Number(v || 0).toFixed(2);

const Metric = ({ label, a, b, testid }) => (
  <div data-testid={testid} className="border border-[#1F2937] rounded p-3 bg-[#050914]">
    <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">{label}</div>
    <div className="mono text-sm text-[#F1F5F9] mt-1">{a}</div>
    {b && <div className="mono text-[10px] text-[#64748B] mt-0.5">{b}</div>}
  </div>
);

const RunRows = ({ run }) => (
  <div className="overflow-x-auto">
    <table data-testid={`oos-run-table-${run.run_id}`} className="w-full mono text-[11px] whitespace-nowrap">
      <thead>
        <tr className="text-[10px] uppercase tracking-widest text-[#64748B] border-b border-[#1F2937]">
          {["Sym", "Bot", "Status", "Backtest net", "2025+ net", "Backtest PF", "2025+ PF", "2025+ DD %", "2025+ trades", "2025+ win %", "Verdict"].map((h, i) => (
            <th key={h} className={`py-2 px-2 ${i < 3 ? "text-left" : "text-right"}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {run.rows.map((r) => {
          const o = r.oos;
          const pass = o && o.compiled_ok && o.net_profit > 0 && o.profit_factor >= 1.1;
          return (
            <tr key={r.bot_name} data-testid={`oos-row-${r.symbol}`} className="border-b border-[#111827]">
              <td className="py-1.5 px-2 text-[#F1F5F9]">{r.symbol}</td>
              <td className="py-1.5 px-2 text-[#94A3B8]">{r.bot_name}</td>
              <td className="py-1.5 px-2" style={{ color: r.status === "done" ? "#2ECA7F" : "#FBBF24" }}>{r.status}</td>
              <td className="py-1.5 px-2 text-right text-[#94A3B8]">{gbp(r.in_sample?.net_profit)}</td>
              <td className="py-1.5 px-2 text-right" style={{ color: o ? (o.net_profit >= 0 ? "#2ECA7F" : "#F87171") : "#64748B" }}>{o ? gbp(o.net_profit) : "-"}</td>
              <td className="py-1.5 px-2 text-right text-[#94A3B8]">{pf(r.in_sample?.profit_factor)}</td>
              <td className="py-1.5 px-2 text-right text-[#F1F5F9]">{o ? pf(o.profit_factor) : "-"}</td>
              <td className="py-1.5 px-2 text-right text-[#F1F5F9]">{o ? Number(o.max_dd_pct || 0).toFixed(1) : "-"}</td>
              <td className="py-1.5 px-2 text-right text-[#F1F5F9]">{o ? o.total_trades : "-"}</td>
              <td className="py-1.5 px-2 text-right text-[#F1F5F9]">{o ? Number(o.win_rate || 0).toFixed(1) : "-"}</td>
              <td className="py-1.5 px-2 text-right" style={{ color: !o ? "#64748B" : pass ? "#2ECA7F" : "#F87171" }}>
                {!o ? "waiting" : pass ? "✓ holds up" : "✗ failed"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const RunBasket = ({ run, mode }) => {
  const v = run.basket?.views?.[mode];
  if (!v) return null;
  const shown = { ...run.basket, monthly: v.monthly, stats: v.stats };
  const st = v.stats;
  return (
    <>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <Metric testid={`oos-${run.run_id}-net`} label="2025+ net profit" a={gbp(st.net_profit)} b={`${st.return_pct}% on ${gbp(st.start_balance)}`} />
        <Metric testid={`oos-${run.run_id}-monthly`} label="Avg £ / month" a={gbp(st.avg_monthly)} b={mode === "compound" ? "compounded" : "fixed lots"} />
        <Metric testid={`oos-${run.run_id}-dd`} label="Max DD (month-end)" a={`${st.max_dd_pct.toFixed(1)}%`} />
        <Metric testid={`oos-${run.run_id}-months`} label="Win / loss months" a={`${st.profit_months} / ${st.losing_months}`} b={`of ${st.months}`} />
        <Metric testid={`oos-${run.run_id}-trades`} label="Trades" a={st.total_trades} b={`win ${st.win_rate}%`} />
      </div>
      <BasketEquityChart result={shown} />
    </>
  );
};

export const OosSection = ({ mode }) => {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => {
    api.get("/mt5admin/portfolio/oos-runs").then((r) => setRuns(r.data.runs || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/mt5admin/portfolio/oos-run");
      setMsg(`Queued ${r.data.queued.filter((q) => q.task_id).length} bots - they run next on the VPS (the brain loop must be running).`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not queue the test");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="oos-section" className="mt-10 border border-[#22D3EE]/30 rounded-lg p-5 bg-[#0B0D14]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-lg font-medium">2025+ out-of-sample test</div>
          <div className="mono text-[10px] text-[#64748B] mt-1">
            Re-runs the basket bots on data they were never tuned on (2025-01-01 to today). This is the honest check.
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="oos-refresh-btn" onClick={load}
            className="mono text-[10px] uppercase tracking-widest px-3 py-2 rounded border border-[#1F2937] text-[#94A3B8] hover:text-[#F1F5F9] transition-colors">Refresh</button>
          <button data-testid="oos-run-btn" onClick={start} disabled={busy}
            className="mono text-[11px] uppercase tracking-widest px-4 py-2 rounded bg-[#22D3EE] text-[#050914] hover:bg-[#67E8F9] disabled:opacity-50 transition-colors">
            {busy ? "Queuing…" : "Run 2025+ test on this basket"}
          </button>
        </div>
      </div>
      {msg && <div data-testid="oos-msg" className="mt-3 mono text-[11px] text-[#22D3EE]">{msg}</div>}
      {runs.length === 0 && <div className="mt-4 mono text-xs text-[#64748B]">No out-of-sample runs yet.</div>}
      {runs.map((run) => (
        <div key={run.run_id} data-testid={`oos-run-${run.run_id}`} className="mt-6 border-t border-[#1F2937] pt-4">
          <div className="mono text-[10px] uppercase tracking-widest text-[#64748B] mb-2">
            {run.run_id} · {run.from_date} → {run.to_date} · {run.done}/{run.rows.length} done · started {run.created_at?.slice(0, 16).replace("T", " ")}
          </div>
          <RunRows run={run} />
          <RunBasket run={run} mode={mode} />
        </div>
      ))}
    </div>
  );
};
